import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FaArrowLeft, FaCheckCircle } from 'react-icons/fa';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  CardFooter,
  Button, 
  OutlineButton, 
  Input, 
  Select, 
  TextArea, 
  FormGroup, 
  Label, 
  ErrorText, 
  Grid,
  FlexContainer
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import IconWrapper from '../common/IconWrapper';
import CustomDialog from '../common/CustomDialog';

const FormContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const FormTitle = styled.h1`
  margin-bottom: 2rem;
  color: #141340;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  .title-icon {
    background: linear-gradient(135deg, #F68B21 0%, #141340 100%);
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.2rem;
    box-shadow: 0 8px 24px rgba(20, 19, 64, 0.3);
  }
`;

const BackNavigation = styled.div`
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(20, 19, 64, 0.08);
  border: 1px solid #f1f3f4;
  
  .back-link {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #141340;
    text-decoration: none;
    font-weight: 600;
    font-size: 0.95rem;
    transition: all 0.3s ease;
    
    &:hover {
      color: #F68B21;
      transform: translateX(-2px);
    }
    
    .back-icon {
      font-size: 1.1rem;
    }
  }
`;

const ProjectInfoCard = styled(Card)`
  margin-bottom: 2rem;
  border: 2px solid #F68B21;
  background: linear-gradient(135deg, #fff9f0 0%, #ffffff 100%);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(246, 139, 33, 0.15);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(246, 139, 33, 0.2);
  }
  
  .project-header {
    background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
    color: white;
    padding: 1.5rem;
    position: relative;
    overflow: hidden;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 150px;
      height: 150px;
      background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
      transform: translate(50%, -50%);
    }
    
    h3 {
      margin: 0;
      font-size: 1.4rem;
      font-weight: 700;
      position: relative;
      z-index: 1;
    }
    
    .project-meta {
      margin-top: 0.75rem;
      font-size: 0.95rem;
      opacity: 0.95;
      position: relative;
      z-index: 1;
    }
  }
`;

const FileInput = styled.div`
  margin-top: 0.5rem;
  
  input {
    display: none;
  }
  
  label {
    display: inline-block;
    padding: 0.5rem 1rem;
    background-color: var(--light-gray);
    color: var(--dark-gray);
    border-radius: var(--border-radius);
    cursor: pointer;
    transition: background-color 0.3s;
    
    &:hover {
      background-color: var(--medium-gray);
    }
  }
`;

const InfoText = styled.p`
  color: var(--dark-gray);
  font-size: 0.9rem;
  margin-top: 0.5rem;
`;

const AnimatedModalOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;
const AnimatedModalContent = styled.div`
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(20,19,64,0.18);
  padding: 2.5rem 2rem 2rem 2rem;
  min-width: 350px;
  max-width: 95vw;
  text-align: center;
  animation: popIn 0.35s cubic-bezier(0.4,0,0.2,1);
  @keyframes popIn {
    0% { transform: scale(0.85); opacity: 0; }
    80% { transform: scale(1.05); opacity: 1; }
    100% { transform: scale(1); opacity: 1; }
  }
`;
const ModalTitle = styled.h2`
  color: #F68B21;
  margin-bottom: 1rem;
`;
const ModalMessage = styled.p`
  color: #141340;
  font-size: 1.1rem;
  margin-bottom: 2rem;
`;
const ModalActions = styled.div`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
`;

const CashierExpenseForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isEditMode = Boolean(id);
  
  // Get projectId from URL query parameters
  const queryParams = new URLSearchParams(location.search);
  const projectId = queryParams.get('projectId');
  
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    category: '',
    date: new Date().toISOString().split('T')[0],
    supplier: '',
    receiptNumber: '',
    receiptType: 'Tax Invoice',
    receiptImage: null as File | null,
    notes: ''
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);
  const [assignedProject, setAssignedProject] = useState<any>(null);
  
  // Dialog state
  const [dialog, setDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

  const showDialog = (title: string, message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setDialog({
      isOpen: true,
      title,
      message,
      type
    });
  };

  const closeDialog = () => {
    setDialog(prev => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    const fetchAssignedProject = async () => {
      try {
        if (projectId) {
          // If projectId is provided in URL, fetch that specific project
          const projectResponse = await apiService.getProject(projectId);
          if (projectResponse.success && projectResponse.data) {
            setAssignedProject(projectResponse.data);
          } else {
            console.error('Project not found:', projectId);
            showDialog('Error', 'Project not found', 'error');
          }
        } else {
          // Get projects assigned to the current cashier
          const assignedProjectsResponse = await apiService.getAssignedProjects();
          const assignedProjects = assignedProjectsResponse.data || [];
          
          if (assignedProjects.length > 0) {
            // For now, use the first assigned project
            // In the future, you might want to show multiple projects or let user select
            setAssignedProject(assignedProjects[0]);
          } else {
            showDialog('Error', 'No projects assigned to you. Please contact your supervisor.', 'error');
          }
        }
        
        if (isEditMode && id) {
          // For edit mode, we'll need to fetch the specific expense
          // Since there's no getExpense method, we'll skip loading existing data for now
          console.log('Edit mode not fully implemented - would need getExpense method');
        }
      } catch (error) {
        console.error("Error fetching assigned project or expense:", error);
        showDialog('Error', 'Failed to load project data', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchAssignedProject();
  }, [isEditMode, id, user?.id, projectId]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!assignedProject) {
    return (
      <Card>
        <CardBody>
          <p>No project assigned. Please contact your supervisor to get assigned to a project.</p>
        </CardBody>
      </Card>
    );
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, receiptImage: file }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};
    
    if (!formData.description) {
      newErrors.description = 'Description is required';
    }
    
    if (parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Amount must be greater than zero';
    }
    
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    
    if (!formData.date) {
      newErrors.date = 'Date is required';
    }
    
    if (!formData.supplier) {
      newErrors.supplier = 'Supplier is required';
    }
    
    if (!formData.receiptNumber) {
      newErrors.receiptNumber = 'Receipt number is required';
    }
    
    if (!formData.receiptType) {
      newErrors.receiptType = 'Receipt type is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      try {
        const formDataToSend = new FormData();
        formDataToSend.append('projectId', assignedProject._id || assignedProject.id);
        formDataToSend.append('description', formData.description);
        formDataToSend.append('amount', formData.amount.toString());
        formDataToSend.append('category', formData.category);
        formDataToSend.append('date', formData.date);
        formDataToSend.append('supplier', formData.supplier);
        formDataToSend.append('receiptNumber', formData.receiptNumber);
        formDataToSend.append('receiptType', formData.receiptType);
        formDataToSend.append('notes', formData.notes);

        if (formData.receiptImage) {
          formDataToSend.append('receiptImage', formData.receiptImage);
        }

        if (isEditMode) {
          // For edit mode, we'll need an updateExpense method
          console.log('Edit mode not fully implemented - would need updateExpense method');
          showDialog('Edit Mode Not Implemented', 'Editing expenses is not fully implemented yet. Please contact your administrator.', 'warning');
        } else {
          console.log('Submitting expense data:', {
            projectId: assignedProject._id || assignedProject.id,
            description: formData.description,
            amount: formData.amount,
            category: formData.category,
            date: formData.date,
            supplier: formData.supplier,
            receiptNumber: formData.receiptNumber,
            receiptType: formData.receiptType,
            notes: formData.notes
          });
          
          const response = await apiService.createExpense(formDataToSend);
          console.log('Expense creation response:', response);
          showDialog('Success', 'Expense recorded successfully!', 'success');
          setTimeout(() => navigate('/cashier'), 2000);
        }
      } catch (error) {
        console.error("Error submitting expense:", error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to record/update expense. Please try again.';
        showDialog('Error', errorMessage, 'error');
      }
    }
  };

  const formatCurrency = (amount: number, currency: string = 'TZS') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <FormContainer>

      
      <FormTitle>
        <div className="title-icon">💰</div>
        {isEditMode ? 'Edit Expense' : 'Record New Expense'}
      </FormTitle>
      
      {assignedProject ? (
        <ProjectInfoCard>
          <div className="project-header">
            <h3>{assignedProject.name}</h3>
            <div className="project-meta">
              Project ID: {assignedProject._id || assignedProject.id} • Budget: {formatCurrency(assignedProject.budget.total)}
            </div>
          </div>
          <CardBody>
            <Grid columns="1fr 1fr" gap="1rem">
              <div>
                <strong>Location:</strong> {assignedProject.location}
              </div>
              <div>
                <strong>Project Manager:</strong> {
                  assignedProject.assignedStaff?.find((staff: any) => staff.role === 'Project Manager')?.name || 'Not assigned'
                }
              </div>
            </Grid>
          </CardBody>
        </ProjectInfoCard>
      ) : (
        <Card>
          <CardBody style={{ textAlign: 'center', padding: '2rem' }}>
            <p>No project assigned. Please contact your supervisor to get assigned to a project.</p>
          </CardBody>
        </Card>
      )}
      
      <Card>
        <form onSubmit={handleSubmit}>
          <CardHeader>
            <h3>Expense Details</h3>
          </CardHeader>
          <CardBody>
            <Grid columns="1fr 1fr" gap="1rem">
              <FormGroup>
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter expense description"
                />
                {errors.description && <ErrorText>{errors.description}</ErrorText>}
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="amount">Amount (TZS)</Label>
                <Input
                  id="amount"
                  name="amount"
                  type="number"
                  value={formData.amount}
                  onChange={handleNumberChange}
                  placeholder="0"
                  min="0"
                  step="1000"
                />
                {errors.amount && <ErrorText>{errors.amount}</ErrorText>}
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="category">Category</Label>
                <Select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                >
                  <option value="">Select Category</option>
                  <option value="Materials">Materials</option>
                  <option value="Labor">Labor</option>
                  <option value="Equipment">Equipment</option>
                  <option value="Sub-Contracts">Sub-Contracts</option>
                  <option value="Others">Others</option>
                </Select>
                {errors.category && <ErrorText>{errors.category}</ErrorText>}
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="date">Expense Date</Label>
                <Input
                  id="date"
                  name="date"
                  type="date"
                  value={formData.date}
                  onChange={handleInputChange}
                />
                {errors.date && <ErrorText>{errors.date}</ErrorText>}
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="supplier">Supplier/Service Provider</Label>
                <Input
                  id="supplier"
                  name="supplier"
                  value={formData.supplier}
                  onChange={handleInputChange}
                  placeholder="Enter supplier name"
                />
                {errors.supplier && <ErrorText>{errors.supplier}</ErrorText>}
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="receiptNumber">Receipt Number</Label>
                <Input
                  id="receiptNumber"
                  name="receiptNumber"
                  value={formData.receiptNumber}
                  onChange={handleInputChange}
                  placeholder="Enter receipt number"
                />
                {errors.receiptNumber && <ErrorText>{errors.receiptNumber}</ErrorText>}
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="receiptType">Receipt Type</Label>
                <Select
                  id="receiptType"
                  name="receiptType"
                  value={formData.receiptType}
                  onChange={handleInputChange}
                >
                  <option value="Tax Invoice">Tax Invoice</option>
                  <option value="Invoice">Invoice</option>
                </Select>
                {errors.receiptType && <ErrorText>{errors.receiptType}</ErrorText>}
              </FormGroup>
            </Grid>
            
            <FormGroup>
              <Label htmlFor="notes">Notes (Optional)</Label>
              <TextArea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Enter any additional notes..."
                rows={3}
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="attachment">Receipt/Invoice Attachment</Label>
              <FileInput>
                <label htmlFor="attachment">
                  {formData.receiptImage ? 'Replace File' : 'Upload File'}
                </label>
                <Input
                  id="attachment"
                  name="attachment"
                  type="file"
                  accept="image/*,.pdf,.doc,.docx"
                  onChange={handleFileChange}
                />
              </FileInput>
              <InfoText>
                Supported formats: Images (JPG, PNG), PDF, Word documents
              </InfoText>
            </FormGroup>
          </CardBody>
          <CardFooter>
            <FlexContainer gap="1rem" justify="flex-end">
              <OutlineButton as={Link} to="/cashier" type="button">
                Cancel
              </OutlineButton>
              <Button type="submit">
                {isEditMode ? 'Update Expense' : 'Record Expense'}
              </Button>
            </FlexContainer>
          </CardFooter>
        </form>
      </Card>
      
      <div style={{ textAlign: 'right', padding: '1rem 0', color: 'var(--dark-gray)', fontSize: '0.8rem' }}>
        Developed by Sandfish
      </div>
      
      {/* Custom Dialog */}
      <CustomDialog
        isOpen={dialog.isOpen}
        onClose={closeDialog}
        title={dialog.title}
        message={dialog.message}
        type={dialog.type}
        confirmText="OK"
        showCancel={false}
      />
    </FormContainer>
  );
};

export default CashierExpenseForm; 